import { useMutation } from '@tanstack/react-query';
import { useDependencies } from '@servicetitan/react-ioc';
import {
    FinancingApi,
    RecalculateOfferData,
    LoanApplicationRead,
} from '@servicetitan/financing-api-client';
import { ServerError } from '@servicetitan/financing-utils-shared';

export interface UseRecalculateOfferOptions {
    onSuccess?: (data: LoanApplicationRead) => void;
    onError?: (error: ServerError) => void;
}

export interface UseRecalculateOfferReturn {
    recalculateOffer: (loanId: string, data: RecalculateOfferData) => void;
    isSubmitting: boolean;
    isSuccess: boolean;
    error: ServerError | null;
    reset: () => void;
}

export const useRecalculateOffer = (
    options: UseRecalculateOfferOptions = {},
): UseRecalculateOfferReturn => {
    const { onSuccess, onError } = options;
    const [financingApi] = useDependencies(FinancingApi);

    const mutation = useMutation<
        LoanApplicationRead,
        ServerError,
        { loanId: string; data: RecalculateOfferData }
    >({
        mutationFn: async ({ loanId, data }) => {
            const response = await financingApi.loansRecalculateOffer(loanId, data);
            return response.data;
        },
        onSuccess: data => {
            onSuccess?.(data);
        },
        onError: error => {
            onError?.(error);
        },
    });

    return {
        recalculateOffer: (loanId: string, data: RecalculateOfferData) =>
            mutation.mutate({ loanId, data }),
        isSubmitting: mutation.isPending,
        isSuccess: mutation.isSuccess,
        error: mutation.error,
        reset: mutation.reset,
    };
};
